/**
 * React-query hooks for the GO Lab Datasets read endpoints.
 *
 * Mirrors the conventions established in
 * `src/components/v4/golab/hooks/useTaxonomy.ts` (factory + `useQuery`)
 * and `src/hooks/useRecords.ts` (`placeholderData` + `retry` skipping 404).
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { APIErrorException } from '../../../../../services/types/errors';
// B17-FE: cross-area import for queue-cache invalidation after a
// successful upload, so freshly-imported records appear on the
// annotation / adjudication queues without a page reload. Mirrors the
// B16-FE precedent of `datasets ↔ annotate` cross-area imports
// (B16-FE went the other direction — annotate types imported by
// datasets); no cycle risk in either direction.
import { annotationKeys } from '../../annotate/hooks/useAnnotation';
import {
  datasetsService,
  type ListCollectionsParams,
} from '../services/datasetsService';
import type {
  ChannelDataset,
  ChannelRecordsList,
  Collection,
  CollectionList,
  Coverage,
  CreateCollectionPayload,
  ListChannelRecordsParams,
  ListChannelUploadsParams,
  Overview,
  Quality,
  SaveSplitPayload,
  Split,
  SplitDetail,
  SubmitUploadPayload,
  SubmitUploadResponse,
  UpdateCollectionPayload,
  UploadHistoryList,
} from '../types';


/**
 * Stable query-key factory for cache invalidation. Uses the
 * `['golab', 'datasets']` namespace because `['datasets']` is already
 * owned by `src/hooks/useDatasets.ts` (the V4 connector-datasets feature).
 */
export const datasetKeys = {
  all: ['golab', 'datasets'] as const,
  collections: () => [...datasetKeys.all, 'collections'] as const,
  collectionList: (params: ListCollectionsParams) =>
    [...datasetKeys.collections(), 'list', params] as const,
  collectionDetail: (id: string) =>
    [...datasetKeys.collections(), 'detail', id] as const,
  channelDatasets: (id: string) =>
    [...datasetKeys.collectionDetail(id), 'channel-datasets'] as const,
  overview: (id: string) =>
    [...datasetKeys.collectionDetail(id), 'overview'] as const,
  coverage: (id: string) =>
    [...datasetKeys.collectionDetail(id), 'coverage'] as const,
  quality: (id: string) =>
    [...datasetKeys.collectionDetail(id), 'quality'] as const,
  // B16-FE: per-channel-dataset records drill-down. Note the cache key
  // is rooted at `datasetKeys.all` (not `collectionDetail`) because
  // channel-dataset ids are globally unique and the modal often opens
  // without a collection context cached alongside.
  channelRecords: (channelDatasetId: string) =>
    [...datasetKeys.all, 'channel-datasets', channelDatasetId, 'records'] as const,
  channelRecordsList: (
    channelDatasetId: string,
    params: ListChannelRecordsParams,
  ) =>
    [...datasetKeys.channelRecords(channelDatasetId), 'list', params] as const,
  // UH-FE: per-channel-dataset upload-history list. Same rationale as
  // `channelRecords` for the root placement — globally-unique cd ids.
  channelUploads: (channelDatasetId: string) =>
    [...datasetKeys.all, 'channel-datasets', channelDatasetId, 'uploads'] as const,
  channelUploadsList: (
    channelDatasetId: string,
    params: ListChannelUploadsParams,
  ) =>
    [...datasetKeys.channelUploads(channelDatasetId), 'list', params] as const,
};


/**
 * Don't retry on 404 (doesn't exist or wrong org — non-transient).
 *
 * 401 is intentionally NOT handled here: `fetchWithAuth` redirects to
 * `/login` and throws a bare `Error('Session expired…')`, never an
 * `APIErrorException`. The redirect interrupts the page before react-query
 * gets a chance to retry, so a 401 branch is unreachable in practice.
 *
 * Other failures retry up to twice (default react-query is 3; we cap
 * at 2 to avoid long waits on real network errors).
 */
const skipRetryOn404 = (failureCount: number, error: unknown): boolean => {
  if (error instanceof APIErrorException && error.status === 404) {
    return false;
  }
  return failureCount < 2;
};


/**
 * Map a mutation error to a user-friendly toast message. Friendly
 * fallbacks for >=500 hide backend internals from end users; specific
 * known-status copy is handled by the caller. Use as the last branch
 * in an `onError` chain.
 */
const friendlyErrorMessage = (error: Error, fallback: string): string => {
  if (error instanceof APIErrorException && error.status >= 500) {
    return 'Something went wrong. Please try again.';
  }
  if (error instanceof APIErrorException) {
    return error.message || fallback;
  }
  return fallback;
};


/**
 * Paginated list of GO Lab dataset collections for the caller's org.
 *
 * NOTE: This is **not** the V4-records `useCollections` from
 * `src/hooks/useRecords.ts` — that one returns record-grouping
 * collections. This one returns GO Lab dataset collections (UK
 * Insurance, etc.). Different module path, different return type.
 * Renamed `useDatasetCollections` so auto-imports can't grab the
 * wrong one.
 *
 * Uses `placeholderData: keepPreviousData` so the previous page stays
 * visible while the next one loads — smooth pagination without flicker.
 */
export const useDatasetCollections = (params: ListCollectionsParams = {}) =>
  useQuery<CollectionList>({
    queryKey: datasetKeys.collectionList(params),
    queryFn: () => datasetsService.listCollections(params),
    // 30s matches the other "live counts" hooks below — internal
    // mutations invalidate via `datasetKeys.collections()`, but a
    // status change from another tab / another user in the same org
    // needs a freshness window short enough that the grid catches up.
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    retry: skipRetryOn404,
  });

export const useCollection = (id: string) =>
  useQuery<Collection>({
    queryKey: datasetKeys.collectionDetail(id),
    queryFn: () => datasetsService.getCollection(id),
    staleTime: 2 * 60 * 1000,
    enabled: !!id,
    retry: skipRetryOn404,
  });

interface TabHookOptions {
  /** Pre-gate the query (default: enabled when the id is non-empty). */
  enabled?: boolean;
}

export const useChannelDatasets = (
  collectionId: string,
  options: TabHookOptions = {},
) =>
  useQuery<ChannelDataset[]>({
    queryKey: datasetKeys.channelDatasets(collectionId),
    queryFn: () => datasetsService.listChannelDatasets(collectionId),
    // Counts and stats can change with annotation activity — shorter
    // staleTime keeps the UI fresh enough.
    staleTime: 30 * 1000,
    enabled: !!collectionId && (options.enabled ?? true),
    retry: skipRetryOn404,
  });

/**
 * B16-FE: paginated records in a single channel dataset, filtered to a
 * closed status set (e.g. Golden = `['finalised']`, Disputed =
 * `['disputed']`, In Progress = the four mid-pipeline statuses).
 *
 * Disabled when `statuses` is empty — the BE would 400, and there's no
 * useful render path for "no filter". Modal callers gate visibility on
 * the same condition.
 *
 * `placeholderData: keepPreviousData` keeps the previous page rendered
 * while the next loads, so users don't see a flash of empty state when
 * paginating.
 */
export const useChannelRecords = (
  channelDatasetId: string,
  params: ListChannelRecordsParams,
) =>
  useQuery<ChannelRecordsList>({
    queryKey: datasetKeys.channelRecordsList(channelDatasetId, params),
    queryFn: () =>
      datasetsService.listChannelRecords(channelDatasetId, params),
    staleTime: 30 * 1000,
    enabled: !!channelDatasetId && params.statuses.length > 0,
    placeholderData: keepPreviousData,
    retry: skipRetryOn404,
  });

/**
 * UH-FE: paginated upload-history audit entries for one channel
 * dataset. Powers `UploadHistoryTable` inside `ChannelDatasetDetail`.
 *
 * `options.enabled` is forwarded so the parent can gate the fetch on
 * `dataset.uploadCount > 0` — empty channels render the empty-state
 * copy without firing a network request.
 *
 * `placeholderData: keepPreviousData` keeps the previous page rendered
 * while the next loads, so users don't see a flash of empty state when
 * paginating.
 */
export const useChannelUploads = (
  channelDatasetId: string,
  params: ListChannelUploadsParams,
  options: { enabled?: boolean } = {},
) =>
  useQuery<UploadHistoryList>({
    queryKey: datasetKeys.channelUploadsList(channelDatasetId, params),
    queryFn: () =>
      datasetsService.listChannelUploads(channelDatasetId, params),
    staleTime: 30 * 1000,
    enabled: !!channelDatasetId && (options.enabled ?? true),
    placeholderData: keepPreviousData,
    retry: skipRetryOn404,
  });

export const useCollectionOverview = (
  collectionId: string,
  options: TabHookOptions = {},
) =>
  useQuery<Overview>({
    queryKey: datasetKeys.overview(collectionId),
    queryFn: () => datasetsService.getCollectionOverview(collectionId),
    staleTime: 30 * 1000,
    enabled: !!collectionId && (options.enabled ?? true),
    retry: skipRetryOn404,
  });

export const useCollectionCoverage = (
  collectionId: string,
  options: TabHookOptions = {},
) =>
  useQuery<Coverage>({
    queryKey: datasetKeys.coverage(collectionId),
    queryFn: () => datasetsService.getCollectionCoverage(collectionId),
    staleTime: 30 * 1000,
    enabled: !!collectionId && (options.enabled ?? true),
    retry: skipRetryOn404,
  });

export const useCollectionQuality = (
  collectionId: string,
  options: TabHookOptions = {},
) =>
  useQuery<Quality>({
    queryKey: datasetKeys.quality(collectionId),
    queryFn: () => datasetsService.getCollectionQuality(collectionId),
    staleTime: 30 * 1000,
    enabled: !!collectionId && (options.enabled ?? true),
    retry: skipRetryOn404,
  });


// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

interface SaveSplitVars {
  channelDatasetId: string;
  payload: SaveSplitPayload;
}

interface SaveSplitContext {
  previousData: ChannelDataset[] | undefined;
}

/**
 * Persist a new split for a channel dataset. Optimistically patches the
 * matching `useChannelDatasets(collectionId)` cache entry while the
 * request is in flight; rolls back on error and replaces with the
 * server's authoritative numbers on success.
 *
 * The optimistic patch deliberately leaves the numeric counts
 * (`training`, `evaluation`) untouched and only flips `lastSplitAt`,
 * `preset`, `ratio`, and `method` on the existing split (if any). Once
 * B1 lands and `finalised` becomes server-derived, computing the
 * counts FE-side would produce wrong-then-right flicker; deferring to
 * the server's response (`onSuccess`) keeps the numbers authoritative.
 */
export const useSaveSplit = (collectionId: string) => {
  const queryClient = useQueryClient();
  return useMutation<SplitDetail, Error, SaveSplitVars, SaveSplitContext>({
    mutationFn: ({ channelDatasetId, payload }) =>
      datasetsService.saveSplit(channelDatasetId, payload),

    onMutate: async ({ channelDatasetId, payload }) => {
      const queryKey = datasetKeys.channelDatasets(collectionId);
      await queryClient.cancelQueries({ queryKey });
      const previousData =
        queryClient.getQueryData<ChannelDataset[]>(queryKey);

      // We only optimistically patch a re-split (where a `Split`
      // already exists on the row) — overlaying the new config fields
      // onto its existing counts. For a brand-new split, building an
      // optimistic object would mean inventing numeric counts the FE
      // can't authoritatively compute (training/evaluation depend on
      // the server-side `finalised` once B1 lands); the flash of
      // 0/0 before `onSuccess` is worse UX than letting the row
      // briefly show the spinner. `onSettled`'s invalidation refreshes
      // either way.
      if (previousData) {
        queryClient.setQueryData<ChannelDataset[]>(
          queryKey,
          previousData.map((cd) => {
            if (cd.id !== channelDatasetId) return cd;
            const existing = cd.split;
            if (!existing) return cd; // first split — skip optimistic patch
            const optimisticSplit: Split = {
              ...existing,
              ratio: payload.ratio,
              method: payload.method,
              preset: payload.preset,
              seed: payload.seed ?? existing.seed,
              lastSplitAt: new Date().toISOString(),
            };
            return { ...cd, split: optimisticSplit };
          }),
        );
      }
      return { previousData };
    },

    onError: (error, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          datasetKeys.channelDatasets(collectionId),
          context.previousData,
        );
      }
      if (error instanceof APIErrorException && error.status === 403) {
        toast.error("You don't have permission to save splits for this collection.");
        return;
      }
      toast.error(friendlyErrorMessage(error, 'Failed to save split.'));
    },

    onSuccess: (data, { channelDatasetId }) => {
      const queryKey = datasetKeys.channelDatasets(collectionId);
      queryClient.setQueryData<ChannelDataset[]>(queryKey, (current) =>
        current?.map((cd) =>
          cd.id === channelDatasetId ? { ...cd, split: data.split } : cd,
        ),
      );
      toast.success('Split saved');
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: datasetKeys.channelDatasets(collectionId),
      });
    },
  });
};


/**
 * Submit an audit-only CSV upload. On success invalidates the
 * collection detail (the `channels` array may have grown) and the
 * channel-datasets list (new upload-history count, possibly a new
 * dataset row). The wizard fires its own success/error toasts so it
 * can include the record count in the copy.
 */
export const useSubmitUpload = (collectionId: string) => {
  const queryClient = useQueryClient();
  return useMutation<SubmitUploadResponse, Error, SubmitUploadPayload>({
    mutationFn: (payload) => datasetsService.submitUpload(payload),
    onSuccess: () => {
      invalidateUploadCaches(queryClient, collectionId);
    },
  });
};

/** Sibling helper exposing the same invalidation set for non-mutation callers. */
export const invalidateUploadCaches = (
  queryClient: QueryClient,
  collectionId: string,
) => {
  queryClient.invalidateQueries({
    queryKey: datasetKeys.collectionDetail(collectionId),
  });
  queryClient.invalidateQueries({
    queryKey: datasetKeys.channelDatasets(collectionId),
  });
  // UH-FE: bust the per-channel-dataset upload-history list (and the
  // records list while we're at it — both live under this prefix and a
  // fresh upload may also have produced new pending records). Caller
  // only knows `collectionId`, not the cd id, so we invalidate the
  // shared root prefix; react-query's hierarchical matcher cascades
  // to every cd's records + uploads list-cache entries. Refetching
  // only happens for mounted queries, so the breadth is harmless.
  queryClient.invalidateQueries({
    queryKey: [...datasetKeys.all, 'channel-datasets'],
  });
  // B17-FE: freshly-imported records appear on the queues immediately
  // (no page reload). react-query's hierarchical key matcher cascades
  // root-key invalidation to every paginated `queueList(params)` /
  // `reviewQueueList(params)` variant — no need to iterate params here.
  queryClient.invalidateQueries({ queryKey: annotationKeys.queue() });
  queryClient.invalidateQueries({ queryKey: annotationKeys.reviewQueue() });
  // B18-FE: pipeline counts strip — fresh upload grows `awaiting`.
  queryClient.invalidateQueries({ queryKey: annotationKeys.pipelineMetrics() });
};

/**
 * Create a new draft collection. On success: seeds the new collection's
 * detail cache (so post-create navigation renders instantly) and
 * invalidates the collection list. 409 collisions surface a friendlier
 * toast than the bare server message.
 */
export const useCreateCollection = () => {
  const queryClient = useQueryClient();
  return useMutation<Collection, Error, CreateCollectionPayload>({
    mutationFn: (payload) => datasetsService.createCollection(payload),
    onSuccess: (collection) => {
      toast.success('Collection created');
      // `seedCollectionCaches` skips the detail-cache write when there's
      // no prior entry (which is always the case for a fresh create —
      // the id didn't exist before). The list invalidation alone is
      // enough; a later `useCollection(id)` fetches the full shape.
      seedCollectionCaches(queryClient, collection);
    },
    onError: (error) => {
      if (error instanceof APIErrorException && error.status === 409) {
        toast.error('A collection with that name already exists.');
        return;
      }
      toast.error(friendlyErrorMessage(error, 'Failed to create collection.'));
    },
  });
};

/**
 * Helper to seed the post-mutation collection detail cache + invalidate
 * the home grid. Shared by create / update / activate / deactivate hooks.
 *
 * The mutation endpoints return `GoLabCollectionRead` (the slim model
 * row), which omits the read-only computed fields `resolvedDimensions`
 * and `staleDimensions` that `GET /collections/{id}` adds. Overwriting
 * the detail cache wholesale would leave those fields `undefined` and
 * crash consumers that spread them (e.g. `Step4Review` does
 * `[...collection.resolvedDimensions]`). So we MERGE onto the prior
 * cache entry — keeping the existing resolved/stale lists, applying
 * the mutation's new fields, and letting the subsequent `invalidate`
 * refetch reconcile any drift authoritatively.
 *
 * When there's no prior cache entry (e.g. the home-grid create flow,
 * where the user hasn't visited the detail page yet), we skip the seed
 * and let the next `useCollection(id)` call fetch the full shape.
 */
const seedCollectionCaches = (
  queryClient: QueryClient,
  collection: Collection,
) => {
  queryClient.setQueryData<Collection>(
    datasetKeys.collectionDetail(collection.id),
    (previous) => (previous ? { ...previous, ...collection } : undefined),
  );
  queryClient.invalidateQueries({ queryKey: datasetKeys.collections() });
};

/**
 * Partial update of a collection's safe fields. 409 → friendly
 * "name already exists" toast; 404 → "not found"; >=500 hidden behind
 * a generic fallback.
 */
export const useUpdateCollection = (collectionId: string) => {
  const queryClient = useQueryClient();
  return useMutation<Collection, Error, UpdateCollectionPayload>({
    mutationFn: (payload) =>
      datasetsService.updateCollection(collectionId, payload),
    onSuccess: (collection) => {
      toast.success('Collection updated');
      seedCollectionCaches(queryClient, collection);
    },
    onError: (error) => {
      if (error instanceof APIErrorException && error.status === 409) {
        toast.error('A collection with that name already exists.');
        return;
      }
      if (error instanceof APIErrorException && error.status === 404) {
        toast.error('Collection not found.');
        return;
      }
      toast.error(friendlyErrorMessage(error, 'Failed to update collection.'));
    },
  });
};

/**
 * Flip draft → active. 422 means the backend's activation gate ("at
 * least one upload exists") fired — surface a copy that points the
 * user at the next step.
 */
export const useActivateCollection = (collectionId: string) => {
  const queryClient = useQueryClient();
  return useMutation<Collection, Error, void>({
    mutationFn: () => datasetsService.activateCollection(collectionId),
    onSuccess: (collection) => {
      toast.success('Collection activated');
      seedCollectionCaches(queryClient, collection);
    },
    onError: (error) => {
      if (error instanceof APIErrorException && error.status === 422) {
        toast.error('Upload at least one CSV before activating this collection.');
        return;
      }
      if (error instanceof APIErrorException && error.status === 404) {
        toast.error('Collection not found.');
        return;
      }
      toast.error(friendlyErrorMessage(error, 'Failed to activate collection.'));
    },
  });
};

/** Flip active → draft. Idempotent on the server. */
export const useDeactivateCollection = (collectionId: string) => {
  const queryClient = useQueryClient();
  return useMutation<Collection, Error, void>({
    mutationFn: () => datasetsService.deactivateCollection(collectionId),
    onSuccess: (collection) => {
      toast.success('Collection deactivated');
      seedCollectionCaches(queryClient, collection);
    },
    onError: (error) => {
      if (error instanceof APIErrorException && error.status === 404) {
        toast.error('Collection not found.');
        return;
      }
      toast.error(friendlyErrorMessage(error, 'Failed to deactivate collection.'));
    },
  });
};

/**
 * Hard-delete the collection. On success, drops the detail cache and
 * invalidates the list; the consumer (page) navigates away.
 */
export const useDeleteCollection = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (collectionId) => datasetsService.deleteCollection(collectionId),
    onSuccess: (_void, collectionId) => {
      toast.success('Collection deleted');
      queryClient.removeQueries({
        queryKey: datasetKeys.collectionDetail(collectionId),
      });
      queryClient.invalidateQueries({ queryKey: datasetKeys.collections() });
    },
    onError: (error) => {
      if (error instanceof APIErrorException && error.status === 404) {
        toast.error('Collection not found.');
        return;
      }
      toast.error(friendlyErrorMessage(error, 'Failed to delete collection.'));
    },
  });
};
